import { Opportunity, OpportunityResponse, NotificationResponse } from "./types";

// const API_URL = 'http://localhost:4000/api/';
const API_URL = 'https://staging.youcould.gg/api/';
const ENDPOINT_LOGIN = 'auth/login';
export const ACCESS_TOKEN_KEY = 'token';
export const REFRESH_TOKEN_KEY = 'refresh_token';

const unauthorizedOpportunities = {
  code: 40100,
  message: 'Unauthorized',
} as OpportunityResponse;

const serverErrorOpportunities = {
  code: 50000,
  message: 'Server error',
} as OpportunityResponse;

export const login = async (username: string, password: string) => {
  const form = new FormData();
  form.append('username', username);
  form.append('password', password);

  const res = await fetch(`${API_URL}${ENDPOINT_LOGIN}`, {
    method: 'POST',
    body: form,
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const detail = data?.detail;
    const message = typeof detail?.message === 'string'
      ? detail.message
      : 'Login failed. Please check your credentials.';
    return { detail: { message } };
  }

  return data;
};

export const oppsQueryString = (props: {
  page?: number;
  filter_query?: string;
  categories?: string[];
  likelihood?: number;
  regional?: boolean;
  closed?: boolean;
}) => {
  const params = new URLSearchParams();
  params.set('page_size', '50');
  params.set('page', String(props.page || 1));

  if (props.filter_query) {
    params.set('filter_query', props.filter_query);
  }
  if (props.likelihood) {
    params.set('likelihood', String(props.likelihood));
  }
  props.categories?.forEach((category) => {
    params.append('categories', category);
  });

  // values:{GR=global/regional, G=global, R=regional}
  params.set(
    'global_opp',
    props.regional === undefined ? 'GR' : props.regional ? 'R' : 'G',
  );

  // values:{LC=live/close, L=live, C=close}
  params.set(
    'opp_status',
    props.closed === undefined ? 'LC' : props.closed ? 'C' : 'L',
  );

  return `/?${params.toString()}`;
};

let refreshInFlight: Promise<number> | null = null;

const refreshAccessToken = async (): Promise<number> => {
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || '';
  if (!storedRefreshToken) {
    return 1;
  }

  const res = await fetch(
    `${API_URL}auth/refresh?refresh_token=${encodeURIComponent(storedRefreshToken)}`,
    { method: 'POST' },
  );

  if (res.status === 401) {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return 1;
  }

  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.access_token) {
    return 1;
  }

  localStorage.setItem(ACCESS_TOKEN_KEY, body.access_token);
  if (body.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, body.refresh_token);
  }
  return 0;
};

export const refreshToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const authorizedFetch = async (url: string, init: RequestInit = {}) => {
  const send = (token: string) => fetch(url, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  const token = localStorage.getItem(ACCESS_TOKEN_KEY) || '';
  const res = await send(token);
  if (res.status !== 401) {
    return res;
  }

  const refreshed = await refreshToken();
  if (refreshed !== 0) {
    return res;
  }

  return send(localStorage.getItem(ACCESS_TOKEN_KEY) || '');
};

const normalizeOpportunityList = (payload: OpportunityResponse): OpportunityResponse => {
  const opps = payload?.opps as unknown;
  if (Array.isArray(opps)) {
    return payload;
  }
  if (opps && typeof opps === 'object') {
    return {
      ...payload,
      opps: Object.values(opps as Record<string, Opportunity>),
    };
  }
  return { ...payload, opps: [] };
};

const taggedOpportunitiesUrl = (status: 'L' | 'C') => {
  const params = new URLSearchParams({
    page: '1',
    page_size: '50',
    opp_status: status,
  });
  return `${API_URL}opp/tagged-opps/?${params.toString()}`;
};

const mergeOpportunities = (lists: Opportunity[][]) => {
  const seen = new Set<string>();
  const merged: Opportunity[] = [];

  lists.flat().forEach((opp) => {
    const id = opp?.id || '';
    if (id && seen.has(id)) {
      return;
    }
    if (id) {
      seen.add(id);
    }
    merged.push(opp);
  });

  return merged;
};

const readOpportunities = async (url: string): Promise<OpportunityResponse> => {
  const res = await authorizedFetch(url);
  if (res.status === 401) {
    return unauthorizedOpportunities;
  }
  if (res.status !== 200) {
    await res.text().catch(() => undefined);
    return serverErrorOpportunities;
  }
  return normalizeOpportunityList(await res.json());
};

export const getAllOpportunities = async (props: {
  type?: "tagged" | "created";
}) => {
  if (props.type === 'tagged') {
    const [live, closed] = await Promise.all([
      readOpportunities(taggedOpportunitiesUrl('L')),
      readOpportunities(taggedOpportunitiesUrl('C')),
    ]);

    if (live.code !== 20000 && closed.code !== 20000) {
      return live;
    }

    const opps = mergeOpportunities([
      live.code === 20000 ? live.opps || [] : [],
      closed.code === 20000 ? closed.opps || [] : [],
    ]);

    return {
      code: 20000,
      message: 'found user opps',
      opps,
      page: {
        page: 1,
        pageSize: opps.length,
        totalItems: opps.length,
      },
    };
  }

  return readOpportunities(`${API_URL}opp/my-opps${oppsQueryString({})}`);
};

const emptyNotifications = (pageSize: number): NotificationResponse => ({
  code: 50000,
  message: 'Server error',
  notifications: [],
  page: { page: 1, pageSize, totalItems: 0 },
});

export const getMyNotifications = async (page: number = 1, pageSize: number = 10): Promise<NotificationResponse> => {
  const res = await authorizedFetch(
    `${API_URL}extra/my-notifications?page=${page}&page_size=${pageSize}`,
  );

  if (res.status === 401) {
    return {
      code: 40100,
      message: 'Unauthorized',
      notifications: [],
      page: { page: 1, pageSize, totalItems: 0 },
    };
  }
  if (res.status !== 200) {
    return emptyNotifications(pageSize);
  }
  return res.json();
};

export const markNotificationAsRead = async (notificationId: string): Promise<boolean> => {
  try {
    const res = await authorizedFetch(
      `${API_URL}extra/my-notifications/update-read?notif_id=${encodeURIComponent(notificationId)}`,
      { method: 'POST' },
    );
    return res.ok;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }
};
